// eslint-disable-next-line import/no-extraneous-dependencies
import { Link } from 'react-router-dom';
import './landing.css';
import shotChoices from './images/shot-choices.webp';
import shotCharacters from './images/shot-characters.webp';
import shotAvatar from './images/shot-avatar.webp';
import shotBranches from './images/shot-branches.webp';

const APP_STORE_URL =
	'https://apps.apple.com/us/app/blushed-romance-choices/id6466787923';
const PLAY_STORE_URL =
	'https://play.google.com/store/apps/details?id=com.sonidorico.blushed&hl=en';

const METRICS = [
	{ value: '5M+', label: 'Downloads on Google Play' },
	{ value: '4.8 ★', label: 'App Store · 11K ratings' },
	{ value: '4.5 ★', label: 'Google Play · 77.1K reviews' },
	{ value: '19', label: 'Languages supported' },
];

const SHOTS = [
	{
		image: shotChoices,
		title: 'Choices that cost you',
		copy: 'Rival empires, one decision.',
	},
	{
		image: shotCharacters,
		title: 'Characters who answer back',
		copy: 'Dialogue written with real authors.',
	},
	{
		image: shotAvatar,
		title: 'Avatar & wardrobe',
		copy: 'Show up as whoever you want to be.',
	},
	{
		image: shotBranches,
		title: 'Walk out or stay',
		copy: 'Every branch keeps going.',
	},
];

const REVIEWS = [
	{
		quote: '“Really loving this game!”',
		body: 'Read five stories so far — “they have all been so amazing.”',
		cite: 'yogurl_17 · App Store',
	},
	{
		quote: '“Each one is engaging and keeps me hooked.”',
		body: 'On the story catalogue, July 2025.',
		cite: 'Danni Renee · Google Play',
	},
	{
		quote: '“There is a lot of stories to choose from.”',
		body: '“I loved the story I got to chose.”',
		cite: 'crystle silva · Google Play',
	},
];

const StoreButtons = ({ variant }) => {
	const appStoreClass = variant === 'cta' ? 'bl-cta-btn' : 'bl-store-btn';
	const playStoreClass =
		variant === 'cta'
			? 'bl-cta-btn bl-cta-btn--ghost'
			: 'bl-store-btn bl-store-btn--alt';

	return (
		<>
			<a
				href={APP_STORE_URL}
				target="_blank"
				rel="noopener noreferrer"
				className={appStoreClass}
			>
				<span className="bl-store-kicker">Download on</span>
				<span className="bl-store-name">the App Store</span>
				{variant !== 'cta' && <span className="bl-sheen" />}
			</a>
			<a
				href={PLAY_STORE_URL}
				target="_blank"
				rel="noopener noreferrer"
				className={playStoreClass}
			>
				<span className="bl-store-kicker">Get it on</span>
				<span className="bl-store-name">Google Play</span>
			</a>
		</>
	);
};

const Landing = () => (
	<div className="bl-scroll">
		<div className="bl-page">
			<header className="bl-header">
				<div className="bl-brand">
					<div className="bl-logo" />
					<span className="bl-brand-name">Blushed</span>
				</div>
				<nav className="bl-nav">
					<a href="#inside" className="bl-nav-link">
						Inside the app
					</a>
					<a href="#reviews" className="bl-nav-link">
						Reviews
					</a>
					<a href="#get" className="bl-cta-pill">
						Get the app
					</a>
				</nav>
			</header>

			<section className="bl-hero">
				<div className="bl-hero-glow" />
				<div className="bl-hero-grid">
					<div>
						<div className="bl-badge">
							<span className="bl-badge-dot" />
							5M+ downloads · 4.8 ★ on the App Store
						</div>
						<h1 className="bl-h1">
							Love stories
							<br />
							you actually <span className="bl-h1-accent">control</span>.
						</h1>
						<p className="bl-lede">
							Blushed is an interactive romance game where every choice writes
							your story — from the first blush to the affairs you
							shouldn&apos;t have. Nineteen languages, hundreds of endings, all
							in your pocket.
						</p>
						<div id="get" className="bl-store-row">
							<StoreButtons />
						</div>
						<div className="bl-stats">
							<div>
								<div className="bl-stat-num">5M+</div>
								<div className="bl-stat-label">Downloads</div>
							</div>
							<div>
								<div className="bl-stat-num">88K</div>
								<div className="bl-stat-label">Ratings</div>
							</div>
							<div>
								<div className="bl-stat-num">19</div>
								<div className="bl-stat-label">Languages</div>
							</div>
						</div>
					</div>
					<div className="bl-phone-col">
						<div className="bl-phone">
							<div className="bl-phone-glow" />
							<div className="bl-phone-frame">
								<div className="bl-phone-screen" />
							</div>
						</div>
					</div>
				</div>
			</section>

			<section className="bl-metrics">
				<div data-reveal-soft="" className="bl-metrics-grid">
					{METRICS.map((metric) => (
						<div key={metric.label} className="bl-metric">
							<div className="bl-metric-num">{metric.value}</div>
							<div className="bl-metric-label">{metric.label}</div>
						</div>
					))}
				</div>
			</section>

			<section id="inside" className="bl-inside">
				<div className="bl-inner">
					<div data-reveal-soft="" className="bl-section-head">
						<h2 className="bl-h2">Inside the app</h2>
						<p className="bl-section-sub">
							Dress your avatar, choose your partner, then live with what you
							picked. Real screens, no mock-ups.
						</p>
					</div>
					<div className="bl-shots">
						<div className="bl-shot-pair">
							{SHOTS.slice(0, 2).map((shot) => (
								<div key={shot.title} className="bl-shot">
									<div
										data-reveal=""
										className="bl-shot-img"
										style={{ backgroundImage: `url('${shot.image}')` }}
									/>
									<div className="bl-shot-title">{shot.title}</div>
									<div className="bl-shot-copy">{shot.copy}</div>
								</div>
							))}
						</div>
						<div className="bl-shot-pair">
							{SHOTS.slice(2).map((shot) => (
								<div key={shot.title} className="bl-shot">
									<div
										data-reveal=""
										className="bl-shot-img"
										style={{ backgroundImage: `url('${shot.image}')` }}
									/>
									<div className="bl-shot-title">{shot.title}</div>
									<div className="bl-shot-copy">{shot.copy}</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</section>

			<section id="reviews" data-reveal-soft="" className="bl-reviews">
				<div className="bl-reviews-grid">
					<div>
						<h2 className="bl-h2">What players say</h2>
						<div className="bl-rating-row">
							<div className="bl-rating-num">4.8</div>
							<div className="bl-rating-side">
								<div className="bl-stars">★★★★★</div>
								<div className="bl-rating-meta">
									out of 5 · 11K ratings on the App Store
								</div>
							</div>
						</div>
						<p className="bl-reviews-note">
							Combined 88K ratings across the App Store and Google Play. Rated
							16+. Published by MILOSERVE ENTERPRISES LTD.
						</p>
					</div>
					<div className="bl-quotes">
						{REVIEWS.map((review) => (
							<figure key={review.cite} data-reveal="" className="bl-quote">
								<blockquote className="bl-quote-text">
									{review.quote}
								</blockquote>
								<p className="bl-quote-body">{review.body}</p>
								<figcaption className="bl-quote-cite">{review.cite}</figcaption>
							</figure>
						))}
					</div>
				</div>
			</section>

			<section className="bl-cta">
				<div className="bl-cta-glow" />
				<div className="bl-cta-inner">
					<h2 className="bl-cta-h2">Your turn to choose.</h2>
					<p className="bl-cta-sub">Free to download on iOS and Android.</p>
					<div className="bl-cta-row">
						<StoreButtons variant="cta" />
					</div>
				</div>
			</section>

			<footer className="bl-footer">
				<div className="bl-footer-inner">
					<span className="bl-footer-brand">
						<span className="bl-footer-logo" />© 2023 MILOSERVE ENTERPRISES LTD
					</span>
					{/* Legal docs stay reachable at /terms-of-use, /privacy-policy and
					    /subscription-terms — they're just not linked from the footer. */}
					<div className="bl-footer-links">
						<Link to="/support" className="bl-footer-link">
							Support
						</Link>
					</div>
				</div>
			</footer>
		</div>
	</div>
);

export default Landing;
